import { createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withDirectives as _withDirectives } from "vue";
// Styles
import "./VWindow.css";

// Components
import { VBtn } from "../VBtn/index.js"; // Composables
import { makeComponentProps } from "../../composables/component.js";
import { useGroup } from "../../composables/group.js";
import { useLocale, useRtl } from "../../composables/locale.js";
import { makeTagProps } from "../../composables/tag.js";
import { makeThemeProps, provideTheme } from "../../composables/theme.js"; // Directives
import vTouch from "../../directives/touch/index.js"; // Utilities
import { computed, nextTick, onScopeDispose, provide, ref, shallowRef, toRef, watch } from 'vue';
import { convertToUnit, genericComponent, IN_BROWSER, isBoolean, PREFERS_REDUCED_MOTION, propsFactory, useRender } from "../../util/index.js";
import { getScrollParent } from "../../util/getScrollParent.js"; // Types
export const VWindowSymbol = Symbol.for('vuetify:v-window');
export const VWindowGroupSymbol = Symbol.for('vuetify:v-window-group');
export const makeVWindowProps = propsFactory({
  continuous: Boolean,
  nextIcon: {
    type: [Boolean, String, Function, Object],
    default: '$next'
  },
  prevIcon: {
    type: [Boolean, String, Function, Object],
    default: '$prev'
  },
  reverse: Boolean,
  showArrows: {
    type: [Boolean, String],
    validator: v => isBoolean(v) || v === 'hover'
  },
  verticalArrows: [Boolean, String],
  wheel: Boolean,
  touch: {
    type: [Object, Boolean],
    default: undefined
  },
  direction: {
    type: String,
    default: 'horizontal'
  },
  modelValue: null,
  disabled: Boolean,
  selectedClass: {
    type: String,
    default: 'v-window-item--active'
  },
  // TODO: mandatory should probably not be exposed but do this for now
  mandatory: {
    type: [Boolean, String],
    default: 'force'
  },
  crossfade: Boolean,
  transitionDuration: Number,
  ...makeComponentProps(),
  ...makeTagProps(),
  ...makeThemeProps()
}, 'VWindow');
export const VWindow = genericComponent()({
  name: 'VWindow',
  directives: {
    vTouch
  },
  props: makeVWindowProps(),
  emits: {
    'update:modelValue': value => true
  },
  setup(props, {
    slots
  }) {
    const {
      themeClasses
    } = provideTheme(props);
    const {
      isRtl
    } = useRtl();
    const {
      t
    } = useLocale();
    const group = useGroup(props, VWindowGroupSymbol);
    const {
      prev: groupPrev,
      next: groupNext
    } = group;
    let stepDirection = 0;
    function step(move, direction) {
      move();
      stepDirection = direction;
      nextTick(() => {
        stepDirection = 0;
      });
    }
    group.prev = () => step(groupPrev, -1);
    group.next = () => step(groupNext, 1);
    const rootRef = ref();
    const isRtlReverse = computed(() => isRtl.value ? !props.reverse : props.reverse);
    const isReversed = shallowRef(false);
    const transition = computed(() => {
      if (props.crossfade) {
        return 'v-window-crossfade-transition';
      }
      const axis = props.direction === 'vertical' ? 'y' : 'x';
      const isAxisReversed = props.direction === 'vertical' ? props.reverse : isRtlReverse.value;
      const reverse = isAxisReversed ? !isReversed.value : isReversed.value;
      const direction = reverse ? '-reverse' : '';
      return `v-window-${axis}${direction}-transition`;
    });
    const transitionCount = shallowRef(0);
    const transitionHeight = ref(undefined);
    const activeIndex = computed(() => {
      return group.items.value.findIndex(item => group.selected.value.includes(item.id));
    });

    // Fix for https://github.com/vuetifyjs/vuetify/issues/18447
    watch(activeIndex, (newVal, oldVal) => {
      let scrollableParent;
      const savedScrollPosition = {
        left: 0,
        top: 0
      };
      if (IN_BROWSER && oldVal >= 0) {
        scrollableParent = getScrollParent(rootRef.value);
        savedScrollPosition.left = scrollableParent?.scrollLeft;
        savedScrollPosition.top = scrollableParent?.scrollTop;
      }
      isReversed.value = stepDirection ? stepDirection < 0 : newVal < oldVal;
      nextTick(() => {
        if (!IN_BROWSER || !scrollableParent) return;
        const currentScrollY = scrollableParent.scrollTop;
        if (currentScrollY !== savedScrollPosition.top) {
          scrollableParent.scrollTo({
            ...savedScrollPosition,
            behavior: 'instant'
          });
        }
        requestAnimationFrame(() => {
          if (!scrollableParent) return;
          const rafScrollY = scrollableParent.scrollTop;
          if (rafScrollY !== savedScrollPosition.top) {
            scrollableParent.scrollTo({
              ...savedScrollPosition,
              behavior: 'instant'
            });
          }
        });
      });
    }, {
      flush: 'sync'
    }); // Run synchronously before DOM updates

    provide(VWindowSymbol, {
      transition,
      isReversed,
      transitionCount,
      transitionHeight,
      transitionDuration: toRef(() => PREFERS_REDUCED_MOTION() ? undefined : convertToUnit(props.transitionDuration, 'ms')),
      rootRef
    });
    const canMoveBack = toRef(() => props.continuous || activeIndex.value !== 0);
    const canMoveForward = toRef(() => props.continuous || activeIndex.value !== group.items.value.length - 1);
    function prev() {
      canMoveBack.value && group.prev();
    }
    function next() {
      canMoveForward.value && group.next();
    }
    let wheelTimeout = -1;
    let nestedEl;
    let wheelAt = 0;
    let isScrollLatchStale = false;
    function lockWheel() {
      window.clearTimeout(wheelTimeout);
      wheelTimeout = window.setTimeout(() => {
        wheelTimeout = -1;
      }, 150);
    }
    function findNestedScroller(target, isVertical, delta) {
      if (!target || !rootRef.value?.contains(target)) return undefined;
      for (let el = target; el && el !== rootRef.value; el = el.parentElement) {
        const style = window.getComputedStyle(el);
        const overflow = isVertical ? style.overflowY : style.overflowX;
        if (overflow !== 'auto' && overflow !== 'scroll') continue;
        const max = isVertical ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth;
        // RTL scrollLeft runs from 0 down to -max
        const position = isVertical ? el.scrollTop : el.scrollLeft + (style.direction === 'rtl' ? max : 0);
        if (delta > 0 ? position < max - 1 : position >= 1) return el;
      }
      return undefined;
    }
    function onWheel(e) {
      if (!props.wheel || props.disabled || e.defaultPrevented || group.items.value.length < 2) return;
      const isVertical = props.direction === 'vertical';
      const scrollDelta = isVertical ? e.shiftKey ? 0 : e.deltaY : e.deltaX || (e.shiftKey ? e.deltaY : 0);
      if (!scrollDelta) return;
      const now = performance.now();
      const gap = now - wheelAt;
      if (gap > 500) isScrollLatchStale = false;
      wheelAt = now;

      // if mouse does not move continuous wheel events get stuck to an element that just left the screen
      const isStale = !e.target.getClientRects().length;
      const isLocked = wheelTimeout >= 0 || transitionCount.value > 0;
      if (!isLocked) {
        const target = isStale ? document.elementFromPoint(e.clientX, e.clientY) : e.target;
        nestedEl = findNestedScroller(target, isVertical, scrollDelta);
      }
      if (nestedEl) {
        if (isStale || isScrollLatchStale) {
          e.preventDefault();
          const scale = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? isVertical ? nestedEl.clientHeight : nestedEl.clientWidth : 1;
          nestedEl.scrollBy({
            [isVertical ? 'top' : 'left']: scrollDelta * scale
          });
        }
        lockWheel();
        return;
      }
      const delta = !isVertical && isRtlReverse.value ? -scrollDelta : scrollDelta;
      const canMove = delta > 0 ? canMoveForward.value : canMoveBack.value;
      if (!canMove && !isLocked) return;
      e.preventDefault();
      lockWheel();
      if (!isLocked) {
        isScrollLatchStale = true;
        delta > 0 ? next() : prev();
      }
    }
    onScopeDispose(() => {
      if (IN_BROWSER) window.clearTimeout(wheelTimeout);
    });
    const arrows = computed(() => {
      const arrows = [];
      const prevProps = {
        icon: isRtl.value && !props.verticalArrows ? props.nextIcon : props.prevIcon,
        class: `v-window__${isRtlReverse.value ? 'right' : 'left'}`,
        onClick: group.prev,
        'aria-label': t('$vuetify.carousel.prev')
      };
      arrows.push(canMoveBack.value ? slots.prev ? slots.prev({
        props: prevProps
      }) : _createVNode(VBtn, prevProps, null) : _createElementVNode("div", null, null));
      const nextProps = {
        icon: isRtl.value && !props.verticalArrows ? props.prevIcon : props.nextIcon,
        class: `v-window__${isRtlReverse.value ? 'left' : 'right'}`,
        onClick: group.next,
        'aria-label': t('$vuetify.carousel.next')
      };
      arrows.push(canMoveForward.value ? slots.next ? slots.next({
        props: nextProps
      }) : _createVNode(VBtn, nextProps, null) : _createElementVNode("div", null, null));
      return arrows;
    });
    const touchOptions = computed(() => {
      if (props.touch === false) return props.touch;
      const options = {
        left: () => {
          isRtlReverse.value ? prev() : next();
        },
        right: () => {
          isRtlReverse.value ? next() : prev();
        },
        start: ({
          originalEvent
        }) => {
          originalEvent.stopPropagation();
        }
      };
      return {
        ...options,
        ...(props.touch === true ? {} : props.touch)
      };
    });
    function onKeyDown(e) {
      const [backKey, forwardKey] = isRtl.value ? ['ArrowRight', 'ArrowLeft'] : ['ArrowLeft', 'ArrowRight'];
      if (props.direction === 'horizontal' && e.key === backKey || props.direction === 'vertical' && e.key === 'ArrowUp') {
        e.preventDefault();
        prev();
        nextTick(() => {
          canMoveBack.value ? focusArrow(0) : focusArrow(1);
        });
      }
      if (props.direction === 'horizontal' && e.key === forwardKey || props.direction === 'vertical' && e.key === 'ArrowDown') {
        e.preventDefault();
        next();
        nextTick(() => {
          canMoveForward.value ? focusArrow(1) : focusArrow(0);
        });
      }
    }
    function focusArrow(index) {
      const arrow = arrows.value[index];
      if (!arrow) return;
      const arrowEl = Array.isArray(arrow) ? arrow[0] : arrow;
      arrowEl.el?.focus();
    }
    useRender(() => _withDirectives(_createVNode(props.tag, {
      "ref": rootRef,
      "class": _normalizeClass(['v-window', {
        'v-window--show-arrows-on-hover': props.showArrows === 'hover',
        'v-window--vertical-arrows': !!props.verticalArrows,
        'v-window--crossfade': !!props.crossfade
      }, themeClasses.value, props.class]),
      "style": _normalizeStyle(props.style),
      "onWheel": onWheel
    }, {
      default: () => [_createElementVNode("div", {
        "class": "v-window__container",
        "style": {
          height: transitionHeight.value
        }
      }, [slots.default?.({
        group
      }), props.showArrows !== false && _createElementVNode("div", {
        "class": _normalizeClass(['v-window__controls', {
          'v-window__controls--left': props.verticalArrows === 'left' || props.verticalArrows === true
        }, {
          'v-window__controls--right': props.verticalArrows === 'right'
        }]),
        "onKeydown": onKeyDown
      }, [arrows.value])]), slots.additional?.({
        group
      })]
    }), [[vTouch, touchOptions.value]]));
    return {
      group
    };
  }
});
//# sourceMappingURL=VWindow.js.map